/**
 * Version History Panel
 *
 * A slide-out panel from the right side showing a tree of canvas version
 * snapshots. Linear chains stay flat; branch points indent children.
 * Allows saving new versions and restoring previous ones. Receives all
 * data and callbacks via props -- no API calls are made from this component.
 */

import { useState, useCallback, useEffect, useRef, memo, useMemo } from 'react'
import { createPortal } from 'react-dom'
import { History, Save, Undo2, Trash2, GitCompareArrows, RefreshCw, X, Clock, User, Bot, Loader2, ArrowLeft } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { cn } from '../../lib/utils'
import { createNadDebug } from '../../utils/debugLog'
import { useBlockingOverlay } from '../../hooks/canvasShortcutGuard'
import { resultOf, trackInteraction } from '../../analytics/interactionAnalytics'
import { useOptionalProjectId } from '../../contexts/ProjectIdContext'

const versionDeleteLog = createNadDebug('dw:canvas-version-history:delete')
const versionRestoreLog = createNadDebug('dw:canvas-version-history:restore')
const versionTreeLog = createNadDebug('dw:canvas-version-history:tree')

/**
 * Sentinel id for the synthetic "current changes" tree row. This node
 * represents the live, unsaved canvas state -- it is NOT a saved snapshot
 * and can never be restored. It must never reach onRestoreVersion /
 * onDeleteVersion / onDiffVersion (which would POST a bogus version id to
 * the backend).
 */
export const WORKING_STATE_ID = '__current_changes__'

export interface VersionEntry {
  version_id: string
  label: string
  created_by: string
  created_at: string
  parents: string[]
}

export interface VersionHistoryPanelProps {
  isOpen: boolean
  onClose: () => void
  versions: VersionEntry[]
  currentVersionId: string | null
  onCreateVersion: (label: string) => Promise<void>
  onRestoreVersion: (versionId: string) => Promise<void>
  onDeleteVersion: (versionId: string) => Promise<void>
  onDiffVersion: (versionId: string) => Promise<void>
  onCloseDiff: () => void
  onRefresh: () => void
  diffText: string | null
  diffVersionId: string | null
  isDiffLoading?: boolean
  isLoading?: boolean
}

/**
 * Format an ISO 8601 timestamp into a locale-aware relative time string.
 *
 * Uses `Intl.RelativeTimeFormat(locale, { numeric: 'auto', style: 'narrow' })`
 * (systemic i18n rec #6) so the unit words ("5m ago", "yesterday", "3d ago", …)
 * and their pluralization/ordering come from the active UI locale rather than
 * hard-coded English. `numeric: 'auto'` yields idiomatic forms like "now" and
 * "yesterday" where the locale defines them; `style: 'narrow'` keeps the
 * compact rendering the timestamp column was designed for (e.g. English
 * "5m ago" / "2h ago" / "3d ago"), avoiding a layout/copy regression vs the
 * prior hard-coded compact strings. The chosen unit mirrors the prior
 * bucketing (seconds → minutes → hours → days → months → years) so the
 * displayed granularity is unchanged; only the language adapts.
 *
 * @param locale The active UI locale (e.g. `i18n.language`).
 */
function formatRelativeTime(iso: string, locale: string): string {
  const now = Date.now()
  const then = new Date(iso).getTime()
  if (Number.isNaN(then)) return iso

  const rtf = new Intl.RelativeTimeFormat(locale, { numeric: 'auto', style: 'narrow' })

  const diffMs = now - then
  // Future or sub-minute timestamps read as the locale's "now".
  if (diffMs < 60_000) return rtf.format(0, 'second')

  const minutes = Math.floor(diffMs / 60_000)
  if (minutes < 60) return rtf.format(-minutes, 'minute')

  const hours = Math.floor(minutes / 60)
  if (hours < 24) return rtf.format(-hours, 'hour')

  const days = Math.floor(hours / 24)
  if (days < 30) return rtf.format(-days, 'day')

  const months = Math.floor(days / 30)
  if (months < 12) return rtf.format(-months, 'month')

  const years = Math.floor(months / 12)
  return rtf.format(-years, 'year')
}

/**
 * Return a display-friendly creator descriptor.
 *
 * For the current user (`icon: 'user'`) the label is rendered from the
 * localized `versionHistoryPanel.labelYou` key at the call site, so this
 * returns an empty label rather than a hard-coded English "You" (the value
 * would never be displayed). Agent/bot creators keep their derived,
 * proper-noun-style label (agent identifiers are not translatable copy).
 */
function formatCreator(createdBy: string): { icon: 'user' | 'bot'; label: string } {
  if (createdBy === 'user') {
    return { icon: 'user', label: '' }
  }
  if (createdBy.startsWith('agent:')) {
    const agentName = createdBy.slice('agent:'.length)
    // Convert camelCase to spaced words
    const label = agentName.replace(/([A-Z])/g, ' $1').trim()
    return { icon: 'bot', label }
  }
  return { icon: 'bot', label: createdBy }
}

// --- Tree layout ---

export type ConnectorType = 'pipe' | 'tee' | 'elbow' | 'space'

export interface TreeRow {
  version: VersionEntry
  depth: number
  connectors: ConnectorType[]
  hasChildren: boolean
  hasParentAbove: boolean
  /**
   * Synthetic "current changes" row representing the live canvas. Derived at
   * display time (see {@link buildDisplayRows}); never produced by
   * {@link buildTreeLayout}. Inert: renders no Restore/Diff/Delete actions.
   */
  isWorkingState?: boolean
}

function DiffViewer({ diff, onClose, versionId, isLoading }: { diff: string | null; onClose: () => void; versionId: string | null; isLoading?: boolean }) {
  const { t } = useTranslation('canvas')
  if (isLoading) {
    return (
      <div className="flex-1 flex items-center justify-center">
        <Loader2 className="w-5 h-5 text-muted-foreground animate-spin" />
      </div>
    )
  }
  if (!diff) return null

  const lines = diff.split('\n')
  return (
    <div className="flex-1 flex flex-col min-h-0">
      <div className="flex items-center justify-between px-4 py-2 border-b border-border shrink-0">
        <button
          onClick={onClose}
          className="flex items-center gap-1.5 text-xs font-medium text-muted-foreground hover:text-foreground transition-colors cursor-pointer"
        >
          <ArrowLeft className="w-3.5 h-3.5" />
          {t('versionHistoryPanel.back')}
        </button>
        <span className="text-xs font-medium text-muted-foreground font-mono truncate ml-2">
          {t('versionHistoryPanel.vsCurrent', { versionId })}
        </span>
      </div>
      <div className="flex-1 overflow-auto px-2 py-1">
        <pre className="text-xxs font-mono leading-relaxed">
          {lines.map((line, i) => {
            let color = 'text-muted-foreground'
            let bg = ''
            if (line.startsWith('+') && !line.startsWith('+++')) {
              color = 'text-green-400'
              bg = 'bg-green-500/10'
            } else if (line.startsWith('-') && !line.startsWith('---')) {
              color = 'text-red-400'
              bg = 'bg-red-500/10'
            } else if (line.startsWith('@@')) {
              color = 'text-blue-400'
              bg = 'bg-blue-500/5'
            } else if (line.startsWith('---') || line.startsWith('+++')) {
              color = 'text-muted-foreground/80'
            }
            return (
              <div key={i} className={cn('px-1 whitespace-pre', color, bg)}>
                {line || ' '}
              </div>
            )
          })}
        </pre>
      </div>
    </div>
  )
}

/**
 * Build a flat array of tree rows from a version list, using the parents
 * field to reconstruct the tree. Single-child chains stay at the same
 * depth (no indentation); multi-child branch points indent all children.
 */
export function buildTreeLayout(versions: VersionEntry[]): TreeRow[] {
  if (!versions.length) return []

  const byId = new Map(versions.map(v => [v.version_id, v]))
  const childrenOf = new Map<string, VersionEntry[]>()
  const roots: VersionEntry[] = []

  for (const v of versions) {
    const parentsInTree = v.parents.filter(p => byId.has(p))
    if (parentsInTree.length === 0) {
      roots.push(v)
    } else {
      const pid = parentsInTree[0]
      if (!childrenOf.has(pid)) childrenOf.set(pid, [])
      childrenOf.get(pid)!.push(v)
    }
  }

  for (const list of childrenOf.values()) {
    list.sort((a, b) =>
      new Date(a.created_at).getTime() - new Date(b.created_at).getTime()
    )
  }

  const result: TreeRow[] = []
  const ancestorContinues: boolean[] = []

  function dfs(v: VersionEntry, depth: number, isLast: boolean, parentAbove: boolean) {
    const connectors: ConnectorType[] = []
    if (parentAbove) {
      // Single-child continuation: only draw ancestor pipes, no branch connector
      for (let i = 0; i < depth; i++) {
        connectors.push(ancestorContinues[i] ? 'pipe' : 'space')
      }
    } else {
      for (let i = 0; i < depth; i++) {
        if (i < depth - 1) {
          connectors.push(ancestorContinues[i] ? 'pipe' : 'space')
        } else {
          connectors.push(isLast ? 'elbow' : 'tee')
        }
      }
    }

    const children = childrenOf.get(v.version_id) ?? []
    result.push({
      version: v,
      depth,
      connectors,
      hasChildren: children.length > 0,
      hasParentAbove: parentAbove,
    })

    if (children.length === 1) {
      dfs(children[0], depth, isLast, true)
    } else {
      for (let i = 0; i < children.length; i++) {
        const isLastChild = i === children.length - 1
        ancestorContinues[depth] = !isLastChild
        dfs(children[i], depth + 1, isLastChild, false)
      }
    }
  }

  for (let i = 0; i < roots.length; i++) {
    dfs(roots[i], 0, i === roots.length - 1, false)
  }

  return result
}

/**
 * Build the ordered list of rows shown in the panel (newest at top), with a
 * synthetic "Current changes" node representing the live canvas.
 *
 * The live canvas is the working state derived from the backend head, so the
 * synthetic node is modeled as a CHILD of the head and laid out by
 * `buildTreeLayout` like any other version. This guarantees it always renders
 * as a leaf branching off the head -- a sibling of any other head children
 * (e.g. a deployment-backup snapshot committed on top of head WITHOUT moving
 * the head pointer). It is therefore never spliced onto an existing edge
 * between two snapshots, which was the RFC-0775 regression: the previous
 * implementation inserted it positionally "above the head" in the reversed
 * list, which only yields a leaf when the head is the newest tip.
 *
 * The node is display-only and non-restorable. Every real snapshot -- including
 * the head -- remains restorable.
 *
 * `headId` is the backend head version id. Returns `[]` when there are no real
 * versions so the empty state still shows.
 */
export function buildDisplayRows(versions: VersionEntry[], headId: string | null): TreeRow[] {
  if (versions.length === 0) return []

  // Model the live canvas as a child leaf of the head. A far-future created_at
  // sorts it as the newest child so it renders as the most-recent branch off
  // the head. The sentinel id is safe: no real version can declare it a parent.
  // If the head is unknown, parents is empty and buildTreeLayout treats the
  // node as a root, so it still renders as a leaf at the top.
  const synthetic: VersionEntry = {
    version_id: WORKING_STATE_ID,
    // The working-state row's visible name is rendered from
    // t('versionHistoryPanel.currentChanges') at display time (isWorkingState),
    // mirroring formatCreator's user branch — so this stored label is never
    // shown and must not hard-code English.
    label: '',
    created_by: 'user',
    created_at: '9999-12-31T23:59:59.999Z',
    parents: headId ? [headId] : [],
  }

  const rows = buildTreeLayout([...versions, synthetic])
  const displayRows = [...rows].reverse()
  for (const row of displayRows) {
    if (row.version.version_id === WORKING_STATE_ID) row.isWorkingState = true
  }
  return displayRows
}

export const VersionHistoryPanel = memo(function VersionHistoryPanel({
  isOpen,
  onClose,
  versions,
  currentVersionId,
  onCreateVersion,
  onRestoreVersion,
  onDeleteVersion,
  onDiffVersion,
  onCloseDiff,
  onRefresh,
  diffText,
  diffVersionId,
  isDiffLoading = false,
  isLoading = false,
}: VersionHistoryPanelProps) {
  const { t, i18n } = useTranslation('canvas')
  // Suspend canvas keyboard shortcuts while this overlay is open (see canvasShortcutGuard).
  // Placed before the `typeof document` SSR early return below so the hook always runs.
  useBlockingOverlay(isOpen)
  const [saveLabel, setSaveLabel] = useState('')
  const [isSaving, setIsSaving] = useState(false)
  const [restoringId, setRestoringId] = useState<string | null>(null)
  const [confirmingId, setConfirmingId] = useState<string | null>(null)
  const [deletingId, setDeletingId] = useState<string | null>(null)
  const [confirmDeleteId, setConfirmDeleteId] = useState<string | null>(null)
  const panelRef = useRef<HTMLDivElement>(null)
  const inputRef = useRef<HTMLInputElement>(null)
  const displayRows = useMemo(
    () => buildDisplayRows(versions, currentVersionId),
    [versions, currentVersionId],
  )

  // NAD trace: make the synthetic "Current changes" placement observable so the
  // leaf-vs-edge regression (RFC-0775) is visible in nad-debug.log.
  useEffect(() => {
    if (!isOpen) return
    const synthetic = displayRows.find((r) => r.isWorkingState)
    versionTreeLog({
      event: 'display-rows-built',
      headId: currentVersionId,
      versionCount: versions.length,
      syntheticParent: currentVersionId,
      syntheticIsLeaf: synthetic ? !synthetic.hasChildren : null,
      syntheticDepth: synthetic ? synthetic.depth : null,
      rowIds: displayRows.map((r) => r.version.version_id).join(','),
    })
  }, [displayRows, isOpen, currentVersionId, versions.length])

  // Focus the input when the panel opens
  useEffect(() => {
    if (isOpen) {
      onRefresh()
      // Small delay to allow transition to start before focusing
      const timer = setTimeout(() => inputRef.current?.focus(), 150)
      return () => clearTimeout(timer)
    }
  }, [isOpen, onRefresh])

  // Close on Escape
  useEffect(() => {
    if (!isOpen) return

    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose()
    }
    document.addEventListener('keydown', handleEscape)
    return () => document.removeEventListener('keydown', handleEscape)
  }, [isOpen, onClose])

  const analyticsProjectId = useOptionalProjectId()
  const trackVersionAction = useCallback((action: 'save' | 'restore' | 'delete' | 'compare', ok: boolean) => {
    if (analyticsProjectId) trackInteraction('canvas_version_history_action', { project_id: analyticsProjectId, action, result: resultOf(ok) })
  }, [analyticsProjectId])

  const handleSave = useCallback(async () => {
    const label = saveLabel.trim() || t('versionHistoryPanel.manualSave')
    setIsSaving(true)
    let ok = false
    try {
      await onCreateVersion(label)
      ok = true
      setSaveLabel('')
    } finally {
      trackVersionAction('save', ok)
      setIsSaving(false)
    }
  }, [saveLabel, onCreateVersion, t, trackVersionAction])

  const handleRestore = useCallback(
    async (versionId: string) => {
      const isHead = versionId === currentVersionId
      const trace_id = `canvas-version-restore:${versionId}`
      // Fail loud: the synthetic "current changes" row is the live canvas and
      // must never be restored. Its row renders no restore button, so this is
      // a defensive guard against a future refactor wiring it up by accident.
      if (versionId === WORKING_STATE_ID) {
        versionRestoreLog({
          event: 'restore-rejected-working-state',
          trace_id,
          versionId,
        })
        return
      }
      if (confirmingId !== versionId) {
        versionRestoreLog({
          event: 'restore-prompt',
          trace_id,
          versionId,
          isHead,
          versionCount: versions.length,
        })
        setConfirmingId(versionId)
        return
      }
      versionRestoreLog({
        event: 'restore-confirm',
        trace_id,
        versionId,
        isHead,
        versionCount: versions.length,
      })
      setConfirmingId(null)
      setRestoringId(versionId)
      try {
        await onRestoreVersion(versionId)
        trackVersionAction('restore', true)
        versionRestoreLog({
          event: 'restore-confirm-success',
          trace_id,
          versionId,
          isHead,
        })
      } catch (err) {
        trackVersionAction('restore', false)
        versionRestoreLog({
          event: 'restore-confirm-failure',
          trace_id,
          versionId,
          isHead,
          errorMessage: err instanceof Error ? err.message : String(err),
        })
        throw err
      } finally {
        setRestoringId(null)
      }
    },
    [onRestoreVersion, confirmingId, currentVersionId, versions, trackVersionAction]
  )

  const handleDelete = useCallback(
    async (versionId: string) => {
      const version = versions.find(v => v.version_id === versionId)
      const isCurrent = versionId === currentVersionId
      const trace_id = `canvas-version-delete:${versionId}`
      if (confirmDeleteId !== versionId) {
        versionDeleteLog({
          event: 'delete-prompt',
          trace_id,
          versionId,
          label: version?.label ?? null,
          isCurrent,
          versionCount: versions.length,
        })
        setConfirmDeleteId(versionId)
        return
      }
      versionDeleteLog({
        event: 'delete-confirm',
        trace_id,
        versionId,
        label: version?.label ?? null,
        isCurrent,
        versionCount: versions.length,
      })
      setConfirmDeleteId(null)
      setDeletingId(versionId)
      try {
        await onDeleteVersion(versionId)
        trackVersionAction('delete', true)
      } catch (err) {
        trackVersionAction('delete', false)
        versionDeleteLog({
          event: 'delete-confirm-failure',
          trace_id,
          versionId,
          isCurrent,
          versionCount: versions.length,
          errorMessage: err instanceof Error ? err.message : String(err),
        })
        throw err
      } finally {
        setDeletingId(null)
      }
    },
    [onDeleteVersion, confirmDeleteId, currentVersionId, versions, trackVersionAction]
  )

  const handleSaveKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (e.key === 'Enter' && !isSaving) {
        e.preventDefault()
        handleSave()
      }
    },
    [isSaving, handleSave]
  )

  if (typeof document === 'undefined') return null

  return createPortal(
    <>
      {/* Backdrop (click to close) */}
      <div
        className={cn(
          'fixed inset-0 z-[1099] bg-black/30 transition-opacity duration-200',
          isOpen ? 'opacity-100' : 'opacity-0 pointer-events-none'
        )}
        onClick={onClose}
        aria-hidden="true"
        data-testid="version-history-backdrop"
      />

      {/* Slide-out panel */}
      <div
        ref={panelRef}
        role="complementary"
        aria-label={t('versionHistoryPanel.versionHistory')}
        className={cn(
          'fixed top-0 right-0 z-[1100] h-full w-80',
          'bg-card border-l border-border shadow-2xl',
          'flex flex-col',
          'transition-transform duration-200 ease-out',
          isOpen ? 'translate-x-0' : 'translate-x-full'
        )}
        data-testid="version-history-panel"
      >
        {/* Header */}
        <div className="flex items-center justify-between px-4 py-3 border-b border-border shrink-0">
          <div className="flex items-center gap-2">
            <History className="w-4 h-4 text-muted-foreground" />
            <h2 className="text-sm font-medium text-foreground">{t('versionHistoryPanel.versionHistory')}</h2>
          </div>
          <div className="flex items-center gap-1">
            <button
              onClick={onRefresh}
              className={cn(
                'w-7 h-7 flex items-center justify-center rounded-md',
                'bg-transparent hover:bg-surface-2 transition-colors',
                'text-muted-foreground hover:text-foreground cursor-pointer'
              )}
              aria-label={t('versionHistoryPanel.refreshVersionHistory')}
              data-testid="version-history-refresh"
            >
              <RefreshCw className="w-3.5 h-3.5" />
            </button>
            <button
              onClick={onClose}
            className={cn(
              'w-7 h-7 flex items-center justify-center rounded-md',
              'bg-transparent hover:bg-surface-2 transition-colors',
              'text-muted-foreground hover:text-foreground cursor-pointer'
            )}
            aria-label={t('versionHistoryPanel.closeVersionHistory')}
            data-testid="version-history-close"
          >
            <X className="w-4 h-4" />
            </button>
          </div>
        </div>

        {/* Save Version section */}
        <div className="px-4 py-3 border-b border-border shrink-0">
          <div className="flex gap-2">
            <input
              ref={inputRef}
              type="text"
              placeholder={t('versionHistoryPanel.versionLabelPlaceholder')}
              value={saveLabel}
              onChange={(e) => setSaveLabel(e.target.value)}
              onKeyDown={handleSaveKeyDown}
              disabled={isSaving}
              className={cn(
                'flex-1 min-w-0 px-3 py-1.5 text-sm bg-muted border border-border rounded-lg',
                'text-foreground placeholder-muted-foreground',
                'focus:outline-none focus:border-primary/50 transition-colors',
                'disabled:opacity-50'
              )}
              data-testid="version-history-label-input"
            />
            <button
              onClick={handleSave}
              disabled={isSaving}
              className={cn(
                'flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm font-medium',
                'bg-primary text-primary-foreground',
                'hover:bg-primary/90 transition-colors cursor-pointer',
                'disabled:opacity-50 disabled:cursor-not-allowed'
              )}
              data-testid="version-history-save-btn"
            >
              {isSaving ? (
                <Loader2 className="w-3.5 h-3.5 animate-spin" />
              ) : (
                <Save className="w-3.5 h-3.5" />
              )}
              {t('versionHistoryPanel.save')}
            </button>
          </div>
        </div>

        {/* Diff viewer or version list */}
        {diffText !== null || isDiffLoading ? (
          <DiffViewer diff={diffText} onClose={onCloseDiff} versionId={diffVersionId} isLoading={isDiffLoading} />
        ) : (
        <div className="flex-1 overflow-y-auto" data-testid="version-history-list">
          {isLoading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="w-5 h-5 text-muted-foreground animate-spin" />
            </div>
          ) : versions.length === 0 ? (
            <div className="px-4 py-12 text-center" data-testid="version-history-empty">
              <Clock className="w-8 h-8 text-muted-foreground/40 mx-auto mb-3" />
              <p className="text-sm text-muted-foreground">
                {t('versionHistoryPanel.noVersionsYet')}
              </p>
              <p className="text-xs font-medium text-muted-foreground/60 mt-1">
                {t('versionHistoryPanel.saveFirstVersion')}
              </p>
            </div>
          ) : (
            <div className="px-4 py-2">
              {displayRows.map((row) => {
                const { version, connectors, hasChildren, hasParentAbove } = row
                // The synthetic "current changes" row is the live canvas: it is
                // emphasized but inert (no restore/diff/delete). Every real
                // snapshot below it -- including the head -- is restorable.
                const isWorking = !!row.isWorkingState
                const isHead = !isWorking && version.version_id === currentVersionId
                const isRestoring = restoringId === version.version_id
                const isDeleting = deletingId === version.version_id
                const isConfirmingRestore = confirmingId === version.version_id
                const isConfirmingDelete = confirmDeleteId === version.version_id
                const creator = formatCreator(version.created_by)

                return (
                  <div
                    key={version.version_id}
                    className="relative flex group"
                    data-testid={`version-entry-${version.version_id}`}
                  >
                    {/* Tree connectors + dot */}
                    <div className="flex shrink-0 self-stretch">
                      {connectors.map((type, i) => (
                        <div key={i} className="relative w-5">
                          {type === 'pipe' && (
                            <div className="absolute left-[9px] top-0 bottom-0 w-px bg-border" />
                          )}
                          {type === 'tee' && (
                            <>
                              <div className="absolute left-[9px] top-0 bottom-0 w-px bg-border" />
                              <div className="absolute left-[9px] top-[7px] h-px bg-border" style={{ width: 11 }} />
                            </>
                          )}
                          {type === 'elbow' && (
                            <>
                              <div className="absolute left-[9px] top-[7px] bottom-0 w-px bg-border" />
                              <div className="absolute left-[9px] top-[7px] h-px bg-border" style={{ width: 11 }} />
                            </>
                          )}
                        </div>
                      ))}
                      {/* Dot column */}
                      <div className="relative w-5">
                        {!hasParentAbove && connectors.length > 0 && (
                          <div className="absolute left-0 top-[7px] w-[9px] h-px bg-border" />
                        )}
                        {hasChildren && (
                          <div className="absolute left-[9px] top-0 h-[7px] w-px bg-border" />
                        )}
                        <div
                          className={cn(
                            'absolute left-[4px] top-[2px] w-2.5 h-2.5 rounded-full border-2',
                            isWorking
                              ? 'bg-primary border-primary'
                              : 'bg-card border-muted-foreground/40'
                          )}
                        />
                        {hasParentAbove && (
                          <div className="absolute left-[9px] top-[14px] bottom-0 w-px bg-border" />
                        )}
                      </div>
                    </div>

                    {/* Version content */}
                    <div className="flex-1 min-w-0 pb-3 pl-2">
                      <div className="flex items-start justify-between gap-2">
                        <div className="min-w-0">
                          <p
                            className={cn(
                              'text-sm truncate',
                              isWorking
                                ? 'font-semibold text-foreground'
                                : 'font-medium text-foreground/90'
                            )}
                            title={isWorking ? t('versionHistoryPanel.currentChanges') : version.label}
                          >
                            {isWorking ? t('versionHistoryPanel.currentChanges') : version.label}
                          </p>
                          {isWorking ? (
                            <div className="flex items-center gap-1.5 mt-0.5">
                              <span className="text-xs font-medium text-muted-foreground/60 truncate">
                                {t('versionHistoryPanel.unsavedLiveCanvas')}
                              </span>
                            </div>
                          ) : (
                            <div className="flex items-center gap-1.5 mt-0.5">
                              {creator.icon === 'user' ? (
                                <User className="w-3 h-3 text-muted-foreground/60 shrink-0" />
                              ) : (
                                <Bot className="w-3 h-3 text-muted-foreground/60 shrink-0" />
                              )}
                              <span className="text-xs font-medium text-muted-foreground/60 truncate">
                                {creator.icon === 'user' ? t('versionHistoryPanel.labelYou') : creator.label}
                              </span>
                              <span className="text-xs font-medium text-muted-foreground/40">
                                ·
                              </span>
                              <span className="text-xs font-medium text-muted-foreground/60 shrink-0">
                                {formatRelativeTime(version.created_at, i18n.language)}
                              </span>
                            </div>
                          )}
                        </div>

                        {/* Actions */}
                        <div className="flex items-center gap-1 shrink-0">
                          {isHead ? (
                            <span className="text-xs text-muted-foreground/70 font-medium shrink-0 py-0.5">
                              {t('versionHistoryPanel.latest')}
                            </span>
                          ) : null}
                          {/* Working-state row is inert: no restore/diff/delete. */}
                          {isWorking ? null : isConfirmingDelete ? (
                            <div className="flex items-center gap-1 shrink-0">
                              <button
                                onClick={() => handleDelete(version.version_id)}
                                disabled={isDeleting}
                                className={cn(
                                  'px-2 py-0.5 rounded text-xs font-medium',
                                  'bg-destructive/10 text-destructive hover:bg-destructive/20',
                                  'transition-colors cursor-pointer',
                                  'disabled:opacity-50 disabled:cursor-not-allowed'
                                )}
                                data-testid={`version-confirm-delete-${version.version_id}`}
                              >
                                {isDeleting ? (
                                  <Loader2 className="w-3 h-3 animate-spin inline mr-1" />
                                ) : null}
                                {t('versionHistoryPanel.delete')}
                              </button>
                              <button
                                onClick={() => setConfirmDeleteId(null)}
                                className="px-1.5 py-0.5 rounded text-xs font-medium text-muted-foreground hover:text-foreground hover:bg-muted transition-colors cursor-pointer"
                              >
                                {t('versionHistoryPanel.cancel')}
                              </button>
                            </div>
                          ) : isConfirmingRestore ? (
                            <div className="flex items-center gap-1 shrink-0">
                              <button
                                onClick={() => handleRestore(version.version_id)}
                                disabled={isRestoring}
                                className={cn(
                                  'px-2 py-0.5 rounded text-xs font-medium',
                                  'bg-destructive/10 text-destructive hover:bg-destructive/20',
                                  'transition-colors cursor-pointer',
                                  'disabled:opacity-50 disabled:cursor-not-allowed'
                                )}
                                data-testid={`version-confirm-restore-${version.version_id}`}
                              >
                                {isRestoring ? (
                                  <Loader2 className="w-3 h-3 animate-spin inline mr-1" />
                                ) : null}
                                {t('versionHistoryPanel.confirm')}
                              </button>
                              <button
                                onClick={() => setConfirmingId(null)}
                                className="px-1.5 py-0.5 rounded text-xs font-medium text-muted-foreground hover:text-foreground hover:bg-muted transition-colors cursor-pointer"
                              >
                                {t('versionHistoryPanel.cancel')}
                              </button>
                            </div>
                          ) : (
                            <div
                              className="flex items-center gap-0.5 shrink-0 opacity-0 group-hover:opacity-100 focus-within:opacity-100"
                              data-testid={`version-actions-${version.version_id}`}
                            >
                              <button
                                onClick={() => handleRestore(version.version_id)}
                                disabled={isRestoring || !!restoringId}
                                className={cn(
                                  'flex items-center gap-1 px-2 py-0.5 rounded text-xs font-medium',
                                  'text-muted-foreground hover:text-foreground',
                                  'hover:bg-muted transition-colors cursor-pointer',
                                  'disabled:opacity-50 disabled:cursor-not-allowed'
                                )}
                                title={t('versionHistoryPanel.restoreToVersion', { label: version.label })}
                                aria-label={t('versionHistoryPanel.restoreToVersion', { label: version.label })}
                                data-testid={`version-restore-${version.version_id}`}
                              >
                                <Undo2 className="w-3 h-3" />
                              </button>
                              <button
                                onClick={() => {
                                  trackVersionAction('compare', true)
                                  void onDiffVersion(version.version_id)
                                }}
                                className={cn(
                                  'flex items-center gap-1 px-1.5 py-0.5 rounded text-xs font-medium',
                                  'text-muted-foreground hover:text-foreground',
                                  'hover:bg-muted transition-colors cursor-pointer'
                                )}
                                title={t('versionHistoryPanel.compareWithCurrent')}
                                aria-label={t('versionHistoryPanel.compareVersionWithCurrent', { label: version.label })}
                                data-testid={`version-diff-${version.version_id}`}
                              >
                                <GitCompareArrows className="w-3 h-3" />
                              </button>
                              <button
                                onClick={() => handleDelete(version.version_id)}
                                disabled={!!deletingId}
                                className={cn(
                                  'flex items-center gap-1 px-1.5 py-0.5 rounded text-xs font-medium',
                                  'text-muted-foreground hover:text-destructive',
                                  'hover:bg-destructive/10 transition-colors cursor-pointer',
                                  'disabled:opacity-50 disabled:cursor-not-allowed'
                                )}
                                title={t('versionHistoryPanel.deleteVersionAndDescendants')}
                                aria-label={t('versionHistoryPanel.deleteVersionLabel', { label: version.label })}
                                data-testid={`version-delete-${version.version_id}`}
                              >
                                <Trash2 className="w-3 h-3" />
                              </button>
                            </div>
                          )}
                        </div>
                      </div>

                      {/* Version ID badge (real snapshots only) */}
                      {isWorking ? null : (
                        <span className="inline-block mt-1 px-1.5 py-0.5 text-xxs text-muted-foreground/50 bg-muted/50 rounded font-mono">
                          {version.version_id}
                        </span>
                      )}
                    </div>
                  </div>
                )
              })}
            </div>
          )}
        </div>
        )}
      </div>
    </>,
    document.body
  )
})
